// Typed API client — single source of truth for backend HTTP calls in panel.
//
// - `apiBase` is read from Vite env (same convention as src/config.ts) and
//   stripped of a trailing slash so callers always pass paths starting with `/`.
// - `apiFetch<T>` returns parsed JSON (typed via the generic) and throws
//   `ApiError` on non-2xx so TanStack Query's error path lights up uniformly.
// - The `token` option is how the request proves who it is (`PanelAuth`, lib/panel-auth.ts):
//   a JWT becomes `Authorization: Bearer …`; the admin's live session (`ADMIN_SESSION`) adds
//   nothing — the browser carries the HttpOnly cookie itself.

import { authHeaders, type PanelAuth } from './panel-auth';

const apiBase = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown
  ) {
    super(`API ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

export interface ApiFetchOptions extends RequestInit {
  /** Чем запрос доказывает, кто он: пропуск → заголовок, сессия админа → кука браузера. */
  token?: PanelAuth | null;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { token, headers, ...rest } = options;
  const res = await fetch(`${apiBase}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
      ...(headers as Record<string, string> | undefined),
    },
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      // Response body wasn't JSON — fine; we still surface status/statusText.
    }
    throw new ApiError(res.status, res.statusText, body);
  }

  // Some endpoints return 204 No Content. Guard against JSON.parse failure.
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export { apiBase };
